import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Input, Modal,
  Option, Select, Sheet, Stack, Table, Textarea, Typography,
} from '@mui/joy';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useAdmin } from '../../AdminContext';
import {
  APIAdminProfiles, APIAdminProfileDetail, APIAdminProfileUpdate,
  APIAdminProfileAccept, APIAdminProfileBlock,
} from '../../api_admin_profiles';
import AiStatusBadge from '../../components/ui/AiStatusBadge';

const STATUS_FILTERS = [
  { value: '', label: 'Все статусы' },
  { value: 'pending', label: 'На модерации' },
  { value: 'accepted', label: 'Приняты' },
  { value: 'blocked', label: 'Заблокированы' },
  { value: 'hidden', label: 'Скрыты' },
];
const AI_FILTERS = [
  { value: '', label: 'ИИ: все' },
  { value: 'red', label: 'ИИ: Не доверяем' },
  { value: 'yellow', label: 'ИИ: Под вопросом' },
  { value: 'green', label: 'ИИ: Допуск' },
  { value: 'none', label: 'ИИ: не проверено' },
];

function StatusChip({ p }) {
  if (p.is_blocked) return <Chip size="sm" color="danger" variant="soft">Заблокирован</Chip>;
  if (p.is_accepted) return <Chip size="sm" color="success" variant="soft">Принят</Chip>;
  if (p.need_moderation) return <Chip size="sm" color="warning" variant="soft">На модерации</Chip>;
  return <Chip size="sm" color="neutral" variant="soft">—</Chip>;
}

function ProfileEditModal({ token, profileId, open, onClose, onSaved }) {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [blockReason, setBlockReason] = useState('');

  useEffect(() => {
    if (!open || !profileId) return;
    let alive = true;
    setLoading(true); setError(null); setBlockReason('');
    (async () => {
      try {
        const r = await APIAdminProfileDetail(token, profileId);
        if (alive) setP(r?.profile || null);
      } catch { if (alive) setError('Не удалось загрузить анкету'); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, profileId, token]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await APIAdminProfileUpdate(token, profileId, {
        name: p.name, age: Number(p.age) || null, city: p.city, region: p.region,
        field_of_activity: p.field_of_activity, message_to_partner: p.message_to_partner,
        is_hidden: !!p.is_hidden,
      });
      onSaved?.();
    } catch { setError('Не удалось сохранить'); } finally { setBusy(false); }
  };
  const accept = async () => { setBusy(true); try { await APIAdminProfileAccept(token, profileId); onSaved?.(); } finally { setBusy(false); } };
  const block = async () => {
    if (!blockReason.trim()) { setError('Укажите причину блокировки'); return; }
    setBusy(true);
    try { await APIAdminProfileBlock(token, profileId, blockReason.trim()); onSaved?.(); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Sheet variant="outlined" sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: { xs: '94%', sm: 560 }, maxHeight: '88vh', overflowY: 'auto', borderRadius: 'lg', p: 3,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography level="h4">Анкета {profileId ? `#${profileId}` : ''}</Typography>
          <IconButton size="sm" variant="plain" onClick={onClose}><CloseRoundedIcon /></IconButton>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress /></Stack>
        ) : !p ? (
          <Alert color="danger">{error || 'Нет данных'}</Alert>
        ) : (
          <Stack spacing={1.3}>
            {error && <Alert color="danger">{error}</Alert>}
            <Stack direction="row" spacing={1} alignItems="center">
              <AiStatusBadge status={p.ai_status} reason={p.ai_reason} confidence={p.ai_confidence} />
              <StatusChip p={p} />
            </Stack>

            <Box><Typography level="body-xs" color="neutral">Имя</Typography>
              <Input value={p.name || ''} onChange={(e) => set('name', e.target.value)} /></Box>
            <Stack direction="row" spacing={1}>
              <Box sx={{ flex: 1 }}><Typography level="body-xs" color="neutral">Возраст</Typography>
                <Input type="number" value={p.age ?? ''} onChange={(e) => set('age', e.target.value)} /></Box>
              <Box sx={{ flex: 2 }}><Typography level="body-xs" color="neutral">Город</Typography>
                <Input value={p.city || ''} onChange={(e) => set('city', e.target.value)} /></Box>
            </Stack>
            <Box><Typography level="body-xs" color="neutral">Регион</Typography>
              <Input value={p.region || ''} onChange={(e) => set('region', e.target.value)} /></Box>
            <Box><Typography level="body-xs" color="neutral">Сфера деятельности</Typography>
              <Input value={p.field_of_activity || ''} onChange={(e) => set('field_of_activity', e.target.value)} /></Box>
            <Box><Typography level="body-xs" color="neutral">Сообщение партнёру</Typography>
              <Textarea minRows={2} value={p.message_to_partner || ''} onChange={(e) => set('message_to_partner', e.target.value)} /></Box>
            <Box><Typography level="body-xs" color="neutral">Видимость в ленте</Typography>
              <Select size="sm" value={p.is_hidden ? 'hidden' : 'visible'}
                onChange={(_, v) => set('is_hidden', v === 'hidden')} sx={{ maxWidth: 240 }}>
                <Option value="visible">Показывается</Option>
                <Option value="hidden">Скрыта</Option>
              </Select></Box>

            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Button color="primary" loading={busy} onClick={save}>Сохранить</Button>
              {!p.is_accepted && <Button color="success" variant="soft" loading={busy} onClick={accept}>Принять</Button>}
            </Stack>

            <Divider />
            <Typography level="title-sm">Блокировка</Typography>
            <Stack direction="row" spacing={1}>
              <Input placeholder="Причина блокировки" value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)} sx={{ flex: 1 }} />
              <Button color="danger" variant="soft" loading={busy} onClick={block}>Заблокировать</Button>
            </Stack>
          </Stack>
        )}
      </Sheet>
    </Modal>
  );
}

export default function AdminProfilesPage() {
  const { adminToken: token } = useAdmin();
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('');
  const [aiF, setAiF] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const r = await APIAdminProfiles(token, {
        search: search || undefined, status: statusF || undefined,
        ai_status: aiF || undefined, page, page_size: 20,
      });
      setItems(r?.items || []); setPages(r?.pages || 1); setTotal(r?.total || 0);
    } catch { setError('Не удалось загрузить анкеты'); } finally { setLoading(false); }
  }, [token, search, statusF, aiF, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Typography level="h4" sx={{ mb: 0.5 }}>Анкеты</Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>Всего: {total}</Typography>

      <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Input size="sm" placeholder="Имя или город" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)} sx={{ flex: 1 }} />
          <Select size="sm" value={statusF} onChange={(_, v) => { setPage(1); setStatusF(v); }} sx={{ minWidth: 170 }}>
            {STATUS_FILTERS.map((f) => <Option key={f.value} value={f.value}>{f.label}</Option>)}
          </Select>
          <Select size="sm" value={aiF} onChange={(_, v) => { setPage(1); setAiF(v); }} sx={{ minWidth: 190 }}>
            {AI_FILTERS.map((f) => <Option key={f.value} value={f.value}>{f.label}</Option>)}
          </Select>
        </Stack>
      </Sheet>

      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
          <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
            <thead>
              <tr>
                <th style={{ width: 70 }}>ID</th><th>Имя</th><th>Город</th>
                <th style={{ width: 80 }}>Возраст</th><th>Статус</th><th>ИИ</th><th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{p.name}</td>
                  <td>{p.city}</td>
                  <td>{p.age}</td>
                  <td><StatusChip p={p} /></td>
                  <td><AiStatusBadge status={p.ai_status} reason={p.ai_reason} confidence={p.ai_confidence} /></td>
                  <td><Button size="sm" variant="soft" onClick={() => setEditId(p.id)}>Открыть</Button></td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7}>
                  <Typography level="body-sm" color="neutral" sx={{ py: 2, textAlign: 'center' }}>Ничего не найдено</Typography>
                </td></tr>
              )}
            </tbody>
          </Table>
        </Sheet>
      )}

      {pages > 1 && (
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
          <Button size="sm" variant="outlined" disabled={page <= 1} onClick={() => setPage((x) => x - 1)}>Назад</Button>
          <Chip variant="soft">{page} / {pages}</Chip>
          <Button size="sm" variant="outlined" disabled={page >= pages} onClick={() => setPage((x) => x + 1)}>Вперёд</Button>
        </Stack>
      )}

      <ProfileEditModal
        token={token} profileId={editId} open={!!editId}
        onClose={() => setEditId(null)} onSaved={() => { setEditId(null); load(); }}
      />
    </Box>
  );
}